<script lang="ts">
import { HgLayout, HgMainNavItem, HgSideMenuNavItem, MenuItem } from '@smg-real-estate/domus';
import { FavouritesLink as SharedFavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { defineComponent, type Component } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import { getFavouritesAuthToken } from '@/helpers/Auth/getFavouritesAuthToken';
import { InteractionEvent, trackEvent, TrackingData } from '@/helpers/GTM/GTMHelper';
import { getAppMetaInfo } from '@/helpers/Meta/metaInfoHelper';
import { getPage } from '@/helpers/Route/routeHelper';
import { handleNavTracking } from '@/helpers/Tracking/Tracking';
import routingMixin from '@/mixins/routingMixin';
import { HG_ACCOUNT_BASE_PATH, HG_BASE_PATH } from '@/router/routes/constants';
import { setTemporaryUUIDCookie } from '@/store/modules/split/services';
import { defaultSmgSplitSession } from '@/store/modules/split/store';
import { initSplitStoreFromSession } from '@smg-real-estate/vue-experiments';
import { fetchFormData } from './helpers/Ssr/listingSSRHelper';

const FavouritesLink = SharedFavouritesLink as Component;

export default defineComponent({
  name: 'AppHg',
  components: {
    FavouritesLink,
    HgLayout,
    HgMainNavItem,
    HgSideMenuNavItem,
  },
  mixins: [routingMixin],
  data() {
    const ctx = useConditionalSSRContext();
    return {
      smgSplitSession: ctx?.smgSplitSession ?? defaultSmgSplitSession,
      baseUrl: import.meta.env.VUE_APP_BASE_URL as string,
      isLoggedIn: false,
      favouritesAuthToken: '',
      passThroughHeaders: ctx?.passThroughHeaders ?? {},
    };
  },
  head() {
    return getAppMetaInfo(this.$i18n.locale);
  },
  computed: {
    ...mapGetters({
      splitSessionKey: 'split/getSplitSessionKey',
    }),
    adMenuItem(): MenuItem {
      return this.$tm('$hg.nav.harmonize.newListingMenuItem') as unknown as MenuItem;
    },
    favouritesMenuItem(): MenuItem {
      return this.$tm('$hg.nav.favoriteMenuItem') as unknown as MenuItem;
    },
    favouritesMainNavItemComponent(): typeof HgMainNavItem {
      return HgMainNavItem;
    },
    favouritesSideMenuNavItemComponent(): typeof HgSideMenuNavItem {
      return HgSideMenuNavItem;
    },
    userAccountMenuItem(): MenuItem {
      return {
        label: this.$tm('$hg.nav.harmonize.overviewMenuItem.label'),
        href: `${HG_ACCOUNT_BASE_PATH}/${this.$i18n.locale}/overview`,
      };
    },
    mainHeaderMenuItem(): MenuItem {
      if (this.isLoggedIn) {
        let privateItems = JSON.parse(JSON.stringify(this.$tm('$hg.nav.privateItems')));
        if (Array.isArray(privateItems)) {
          privateItems = privateItems.map((item) => {
            let modifiedHref;

            if (
              item.href.includes('messenger') ||
              item.href.includes('my-ads') ||
              item.trackingLabel.includes('searchAlertSettings') ||
              item.href.includes('my-realestate')
            ) {
              modifiedHref = `${HG_BASE_PATH}${item.href}`;
            } else {
              modifiedHref = `${HG_ACCOUNT_BASE_PATH}${item.href}`;
            }

            return { ...item, href: modifiedHref };
          });
        }
        return {
          label: this.$tm('$hg.nav.loggedInMenuItem.label'),
          children: privateItems,
        };
      } else {
        return {
          label: this.$tm('$hg.nav.harmonize.overviewMenuItem.label'),
          href: `${HG_ACCOUNT_BASE_PATH}/${this.$i18n.locale}/overview`,
        };
      }
    },
    isFooterHidden(): boolean {
      return true;
    },
    isProduction(): boolean {
      return import.meta.env.VUE_APP_STAGE === 'prod';
    },
  },
  watch: {
    '$route.path': {
      handler(path: string) {
        this.setCurrentPage(getPage(path));
        fetchFormData(this.$store, this.$route, this.passThroughHeaders);
      },
    },
  },
  async mounted() {
    window.addEventListener('optanonHandler', () => {
      setTemporaryUUIDCookie(this.splitSessionKey, document.cookie, window.location.href);
      this.updateSplitStoreFromApi();
    });
    this.updateUserDetails();
    this.favouritesAuthToken = await getFavouritesAuthToken(this.$auth0);
  },
  methods: {
    ...mapActions({
      updateSplitStoreFromApi: 'split/updateSplitStoreFromApi',
    }),
    ...mapMutations({
      setCurrentPage: 'common/setCurrentPage',
      setLoggedInUser: 'common/setLoggedInUser',
    }),
    handleTrackFooterClick(data: TrackingData) {
      trackEvent(InteractionEvent.FOOTER_CLICK, {
        h_event_category_006: 'footer_interaction',
        h_event_action_007: data.eventAction,
        h_event_label_008: data.eventLabel,
        non_interaction: false,
      });
    },
    updateUserDetails() {
      if (this.$auth0?.isAuthenticated.value) {
        this.isLoggedIn = true;
        this.setLoggedInUser(this.$auth0.user.value['cognito:username']);
      } else {
        this.isLoggedIn = false;
      }
    },
    handleMainNavTracking(menuItem: MenuItem, event: Event) {
      handleNavTracking(menuItem, event, false, this.$i18n.locale);
    },
    handleMobileMenuNavTracking(menuItem: MenuItem, event: Event) {
      handleNavTracking(menuItem, event, true, this.$i18n.locale);
    },
  },
  async serverPrefetch(): Promise<void> {
    this.setCurrentPage(getPage(this.$route.path));
    initSplitStoreFromSession(this.$store, this.smgSplitSession);
    await fetchFormData(this.$store, this.$route, this.passThroughHeaders);
  },
});
</script>

<template>
  <div id="app">
    <HgLayout
      :baseUrl="baseUrl"
      :currentLanguage="$i18n.locale"
      :currentRoute="$route"
      :getLanguageToLink="getLanguageSwitchLink"
      :hideFooter="isFooterHidden"
      @trackFooterClick="handleTrackFooterClick"
    >
      <template #headerMetaAction>
        <HgMainNavItem
          :menuItem="adMenuItem"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          :bordered="true"
        />
      </template>
      <template #headerMetaNavigation>
        <FavouritesLink
          :menuItem="favouritesMenuItem"
          platform="hg"
          :menuNavItem="favouritesMainNavItemComponent"
          :isProduction="isProduction"
          :baseUrl="baseUrl"
          :authToken="favouritesAuthToken"
          :route="$route"
          @mainNavItemClick="handleMainNavTracking"
        />
        <HgMainNavItem
          :menuItem="mainHeaderMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          dropdownOrientation="right"
          :currentRoute="$route"
          @mainNavItemClick="handleMainNavTracking"
          @dropDownMenuItemClick="handleMainNavTracking"
        />
      </template>
      <template #sideMenuMetaNavigation>
        <FavouritesLink
          :menuItem="favouritesMenuItem"
          platform="hg"
          :menuNavItem="favouritesSideMenuNavItemComponent"
          :isProduction="isProduction"
          :baseUrl="baseUrl"
          :authToken="favouritesAuthToken"
          :route="$route"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="userAccountMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          :currentRoute="$route"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
      </template>
      <router-view />
    </HgLayout>
  </div>
</template>

<style lang="scss">
@use '@/assets/globals-hg' as *;

#side-menu-language-switch a {
  &.router-link-exact-active {
    border-bottom: 0;
  }
}
</style>
